import RecipeImage from '../components/RecipeImage';
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { isFamilyFilterActive, getEditorIds, filterByFamily } from '../utils/familyFilter';
import {
    Avatar,
    Box,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Grid,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ListIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import '../App.css';
import { RECIPE_TYPES } from '../constants/Constants';

const FILTERS = [
    { key: 'all',        label: 'All' },
    { key: 'title',      label: 'Title' },
    { key: 'author',     label: 'Author' },
    { key: 'ingredient', label: 'Ingredient' },
];

function searchRecipes(data, searchWord, filter) {
    const results = [];
    const q = searchWord.toLowerCase().replace(/\s+/g, ' ').trim();

    Object.values(data.recipes).forEach((recipes) => {
        recipes.forEach((recipe) => {
            const matchReasons = [];

            if (filter === 'all' || filter === 'title') {
                if (recipe.title?.toLowerCase().includes(q)) {
                    matchReasons.push({ type: 'title', label: 'Title' });
                }
            }

            if (filter === 'all' || filter === 'author') {
                const matchedAuthor = recipe.authors?.length > 0
                    ? recipe.authors.find(a => a.display_name?.toLowerCase().includes(q))
                    : recipe.author?.toLowerCase().includes(q) ? { display_name: recipe.author } : null;
                if (matchedAuthor) {
                    matchReasons.push({ type: 'author', label: `Author: ${matchedAuthor.display_name}` });
                }
            }

            if (filter === 'all' || filter === 'ingredient') {
                const matchedIng = (recipe.ingredients || []).find(
                    (ing) => ing.ingredient?.toLowerCase().includes(q)
                );
                if (matchedIng) {
                    matchReasons.push({ type: 'ingredient', label: `Ingredient: ${matchedIng.ingredient}` });
                }
            }

            if (matchReasons.length > 0 && !results.find(r => r.id === recipe.id)) {
                results.push({
                    id: recipe.id,
                    title: recipe.title,
                    author: recipe.author,
                    author_id: recipe.author_id,
                    author_user_id: recipe.author_user_id,
                    authors: recipe.authors,
                    download_image_url: recipe.download_image_url,
                    recipe_type_id: recipe.recipe_type_id,
                    created_by: recipe.created_by,
                    matchReasons,
                });
            }
        });
    });

    return results.sort((a, b) => a.title.localeCompare(b.title));
}

function capitalizeWords(str) {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
}

const MATCH_COLORS = {
    title:      { bgcolor: 'primary.main',   color: '#fff' },
    author:     { bgcolor: 'secondary.dark', color: 'primary.main' },
    ingredient: { bgcolor: 'success.light',  color: '#fff' },
};

const VIEW_MODE_KEY = 'recipeViewMode';

const SearchResults = () => {
    const { search_value } = useParams();
    const navigate = useNavigate();
    const [filter, setFilter] = useState('title');
    const [inputValue, setInputValue] = useState(search_value ?? '');
    const [viewMode, setViewMode] = useState(localStorage.getItem(VIEW_MODE_KEY) || 'grid');
    const [typeFilter, setTypeFilter] = useState(null);

    const handleViewMode = (_, newMode) => {
        if (!newMode) return;
        setViewMode(newMode);
        localStorage.setItem(VIEW_MODE_KEY, newMode);
    };
    const [recipeList, setRecipeList] = useState(() =>
        JSON.parse(sessionStorage.getItem('recipeList'))
    );
    const [authors, setAuthors] = useState([]);
    const [users, setUsers] = useState([]);
    const [editorIds, setEditorIds] = useState(new Set());
    const [loading, setLoading] = useState(!recipeList);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user')) || {};
        if (!['editor', 'admin'].includes(user.role)) return;
        getEditorIds().then(setEditorIds);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // If cache is empty (e.g. direct URL navigation), fetch it
    useEffect(() => {
        if (recipeList) return;
        const api = `${process.env.REACT_APP_BACKEND_ADDRESS}/recipes`;
        fetch(api, {
            method: 'GET',
            headers: { 'Authorization': 'Bearer ' + Cookies.get('token') },
        })
            .then(r => r.json())
            .then(data => {
                sessionStorage.setItem('recipeList', JSON.stringify(data));
                setRecipeList(data);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [recipeList]);

    useEffect(() => {
        const headers = { 'Authorization': 'Bearer ' + Cookies.get('token') };
        Promise.all([
            fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/authors`, { headers }).then(r => r.json()),
            fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/users`, { headers }).then(r => r.json()),
        ]).then(([authorData, userData]) => {
            setAuthors(Array.isArray(authorData) ? authorData : []);
            setUsers(userData?.users ?? []);
        }).catch(console.error);
    }, []);

    // Sync input and reset filters when URL search term changes
    useEffect(() => {
        setInputValue(search_value ?? '');
        setFilter('all');
        setTypeFilter(null);
    }, [search_value]);

    const handleSearch = (e) => {
        if (e.key === 'Enter' && inputValue.trim()) {
            navigate(`/search/${encodeURIComponent(inputValue.trim())}`);
        }
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
            <CircularProgress />
        </Box>
    );

    if (!recipeList) return (
        <Container component="main" sx={{ mt: 5, width: '90%', pb: 12 }}>
            <Typography color="text.secondary">Could not load recipes. Please try again.</Typography>
        </Container>
    );

    const rawResults = searchRecipes(recipeList, search_value, filter);
    const familyFiltered = isFamilyFilterActive() ? filterByFamily(rawResults, editorIds) : rawResults;
    const searchResults = typeFilter
        ? familyFiltered.filter(r => r.recipe_type_id === typeFilter)
        : familyFiltered;
    const allResults = familyFiltered;

    const q = (search_value ?? '').toLowerCase().trim();

    // Merge authors table + users table into one searchable list, deduped.
    // Authors get an /author/:id link; users get /profile/:id.
    // A user who is also a named author profile appears once (as the author entry).
    const authorUserIds = new Set(authors.map(a => a.created_by).filter(Boolean));
    const peopleList = [
        ...authors.map(a => ({
            key: `author-${a.id}`,
            display_name: a.name,
            avatar_url: a.avatar_url ?? null,
            bio: a.bio ?? null,
            to: `/author/${a.id}`,
        })),
        ...users
            .filter(u => !authorUserIds.has(u.id))
            .map(u => ({
                key: `user-${u.id}`,
                display_name: u.display_name || `${u.first_name} ${u.last_name}`,
                avatar_url: u.avatar_url ?? null,
                bio: null,
                to: `/profile/${u.id}`,
            })),
    ];

    const matchedAuthors = (filter === 'all' || filter === 'author')
        ? peopleList.filter(p => p.display_name?.toLowerCase().includes(q))
        : [];

    // Only show type filter chips for types that appear in results
    const activeTypes = RECIPE_TYPES.filter(t => allResults.some(r => r.recipe_type_id === t.id));

    return (
        <Container component="main" sx={{ mt: 5, width: '90%', pb: 12 }}>
            {/* Inline search bar */}
            <TextField
                fullWidth
                size="small"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleSearch}
                placeholder="Search recipes…"
                sx={{ mb: 2 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                }}
            />

            <Grid container alignItems="center" sx={{ mb: 1 }}>
                <Grid item xs>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Results for "{capitalizeWords(search_value)}"
                    </Typography>
                </Grid>
                <Grid item>
                    <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewMode} size="small">
                        <ToggleButton value="grid"><GridViewIcon fontSize="small" /></ToggleButton>
                        <ToggleButton value="list"><ListIcon fontSize="small" /></ToggleButton>
                    </ToggleButtonGroup>
                </Grid>
            </Grid>
            <Divider sx={{ mb: 2 }} />

            {/* Author results */}
            {matchedAuthors.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Authors
                    </Typography>
                    <List disablePadding>
                        {matchedAuthors.map((person) => (
                            <ListItem key={person.key} disablePadding divider>
                                <ListItemButton component={Link} to={person.to}>
                                    <ListItemAvatar>
                                        <Avatar
                                            src={person.avatar_url || undefined}
                                            alt={person.display_name}
                                            sx={{ width: 36, height: 36 }}
                                        />
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={person.display_name}
                                        secondary={person.bio || null}
                                        secondaryTypographyProps={{ noWrap: true }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                    <Divider sx={{ mt: 2, mb: 2 }} />
                </Box>
            )}

            {/* Search field filters */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                {FILTERS.map(({ key, label }) => (
                    <Chip
                        key={key}
                        label={label}
                        onClick={() => setFilter(key)}
                        variant={filter === key ? 'filled' : 'outlined'}
                        color={filter === key ? 'primary' : 'default'}
                        size="small"
                    />
                ))}
            </Box>

            {/* Recipe type filters — only shown when results exist */}
            {activeTypes.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                    {activeTypes.map((type) => (
                        <Chip
                            key={type.id}
                            label={`${type.icon} ${type.title}`}
                            onClick={() => setTypeFilter(typeFilter === type.id ? null : type.id)}
                            size="small"
                            sx={{
                                bgcolor: typeFilter === type.id ? type.color : 'transparent',
                                borderColor: type.color,
                                color: typeFilter === type.id ? '#fff' : 'text.primary',
                                border: '1px solid',
                                fontWeight: typeFilter === type.id ? 600 : 400,
                            }}
                        />
                    ))}
                </Box>
            )}

            {searchResults.length > 0 ? (
                <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                    </Typography>
                    {viewMode === 'grid' ? (
                        <Grid container spacing={2}>
                            {searchResults.map((recipe) => (
                                <Grid item key={recipe.id} xs={12} sm={6}>
                                    <Link to={`/recipe/${recipe.id}`} state={{ id: recipe.id }} style={{ textDecoration: 'none', color: 'inherit' }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                p: 1.5,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                                                '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' },
                                            }}
                                        >
                                            <RecipeImage
                                                loading="lazy"
                                                src={recipe.download_image_url || '/anchovie.jpg'}
                                                alt={recipe.title}
                                                sx={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0 }}
                                            />
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }} noWrap>
                                                    {recipe.title}
                                                </Typography>
                                                {(recipe.authors?.length > 0 || recipe.author) && (
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        Recipe Courtesy of {recipe.authors?.length > 0
                                                            ? recipe.authors.map(a => a.display_name).join(' & ')
                                                            : recipe.author}
                                                    </Typography>
                                                )}
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
                                                    {(() => { const t = RECIPE_TYPES.find(t => t.id === recipe.recipe_type_id); return t ? <Chip label={`${t.icon} ${t.title}`} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: t.color, color: '#fff' }} /> : null; })()}
                                                    {recipe.matchReasons.map((reason, i) => (
                                                        <Chip
                                                            key={i}
                                                            label={reason.label}
                                                            size="small"
                                                            sx={{ height: 20, fontSize: '0.7rem', ...MATCH_COLORS[reason.type] }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Link>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <List disablePadding>
                            {searchResults.map((recipe) => (
                                <ListItem key={recipe.id} disablePadding divider>
                                    <ListItemButton component={Link} to={`/recipe/${recipe.id}`} state={{ id: recipe.id }} sx={{ gap: 1.5 }}>
                                        <RecipeImage
                                            loading="lazy"
                                            src={recipe.download_image_url || '/anchovie.jpg'}
                                            alt={recipe.title}
                                            sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                                        />
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }} noWrap>
                                                {recipe.title}
                                            </Typography>
                                            {recipe.author && (
                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                    Recipe Courtesy of {recipe.author}
                                                </Typography>
                                            )}
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                                {(() => { const t = RECIPE_TYPES.find(t => t.id === recipe.recipe_type_id); return t ? <Chip label={`${t.icon} ${t.title}`} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: t.color, color: '#fff' }} /> : null; })()}
                                                {recipe.matchReasons.map((reason, i) => (
                                                    <Chip
                                                        key={i}
                                                        label={reason.label}
                                                        size="small"
                                                        sx={{ height: 20, fontSize: '0.7rem', ...MATCH_COLORS[reason.type] }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </>
            ) : (
                <Box sx={{ textAlign: 'center', mt: 8 }}>
                    <SearchOffIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No results for "{capitalizeWords(search_value)}"
                    </Typography>
                    <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                        Try a different search term or filter
                    </Typography>
                </Box>
            )}
        </Container>
    );
};

export default SearchResults;
